import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { pharmacyApi } from '../services/api/pharmacy.api';
import { useRecentPatientSearchesStore } from './recentPatientSearchesStore';
import type { PmsDatabaseType } from '../types/pms';

// TODO: One pharmacy has two duplicate objects in the system (62ea8245448a8b57f3f80e74
// and 64662eb695bfef1e99ffbed6). This workaround does three things:
//   1. PHARMACY_ID_SWAPS — auto-redirects any selection of the stale ID to the correct one
//   2. HIDDEN_PHARMACY_IDS — hides the stale pharmacy from the pharmacy selector list
//   3. Swap logic in setActivePharmacy, getActivePharmacy, and rehydration to catch all entry points
// Remove all of this once the duplicate record (62ea8245448a8b57f3f80e74) is cleaned up
// or merged in the database.
const PHARMACY_ID_SWAPS: Record<string, string> = {
  '62ea8245448a8b57f3f80e74': '64662eb695bfef1e99ffbed6',
};
const HIDDEN_PHARMACY_IDS = new Set(Object.keys(PHARMACY_ID_SWAPS));

/** Returns the swapped pharmacy ID if a redirect is configured, otherwise the original. */
const applyPharmacyIdSwap = (id: string): string => PHARMACY_ID_SWAPS[id] ?? id;

export interface Pharmacy {
  id: string;
  name: string;
  code?: string;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  phone?: string;
  fax?: string;
  email?: string;
  isActive: boolean;
  features?: { [key: string]: boolean };
  /** PRX-26: pharmacy-managed branding — uploaded logo as a data URL, absent when none. */
  branding?: { logoDataUrl?: string | null };
  /** PRX-31: PMS dialect — Kroll-only UI (central fill, Kroll image types) adapts on 'fillware'. */
  databaseType?: PmsDatabaseType | null;
}

/**
 * Get the location identifier for a pharmacy (uses city field)
 * Defaults to 'Mississauga' if city is not set
 */
export const getPharmacyLocation = (pharmacy: Pharmacy | null): string => {
  return pharmacy?.city || 'Mississauga';
};

interface PharmacyState {
  // State
  pharmacies: Pharmacy[];
  activePharmacy: Pharmacy | null;
  loading: boolean;
  error: string | null;
  userId: string | null;
  isHydrated: boolean;

  // Computed properties
  pharmacyId: string | null;
  selectedPharmacyId: string | null;

  // Actions
  fetchPharmacies: () => Promise<void>;
  setActivePharmacy: (pharmacyId: string) => Promise<void>;
  getActivePharmacy: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
  setUserId: (userId: string) => void;
}

const initialState = {
  pharmacies: [],
  activePharmacy: null,
  loading: false,
  error: null,
  userId: null as string | null,
  pharmacyId: null as string | null,
  selectedPharmacyId: null as string | null,
  isHydrated: false,
};

// Track hydration state globally
let hasHydrated = false;
const hydrationPromise = new Promise<void>((resolve) => {
  const checkHydration = setInterval(() => {
    if (hasHydrated) {
      clearInterval(checkHydration);
      resolve();
    }
  }, 10);
});

export const waitForHydration = () => hydrationPromise;

export const usePharmacyStore = create<PharmacyState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Computed properties - these are regular properties that get computed
        pharmacyId: null,
        selectedPharmacyId: null,
        isHydrated: false,

        fetchPharmacies: async () => {
          set({ loading: true, error: null });
          try {
            const allPharmacies = await pharmacyApi.getUserPharmacies();
            const pharmacies = allPharmacies.filter((p) => !HIDDEN_PHARMACY_IDS.has(p.id));
            set({ pharmacies, loading: false });

            // Always try to get the active pharmacy after fetching pharmacies
            // The backend will auto-select the first one if none is active
            await get().getActivePharmacy();
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Failed to fetch pharmacies',
              loading: false,
            });
          }
        },

        setActivePharmacy: async (pharmacyId: string) => {
          const resolvedId = applyPharmacyIdSwap(pharmacyId);
          const previousActiveId = get().activePharmacy?.id;
          set({ loading: true, error: null });
          try {
            const pharmacy = await pharmacyApi.setActivePharmacy(resolvedId);
            set({
              activePharmacy: pharmacy,
              pharmacyId: pharmacy.id,
              selectedPharmacyId: pharmacy.id,
              loading: false,
            });
            // When the active pharmacy actually changes, drop the recent-patient
            // search history — those patients belong to the previous pharmacy and
            // must not leak across the tenant boundary (multi-tenant isolation).
            // One source of truth: every caller of setActivePharmacy gets this.
            if (previousActiveId && previousActiveId !== pharmacy.id) {
              useRecentPatientSearchesStore.getState().clear();
            }
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Failed to set active pharmacy',
              loading: false,
            });
          }
        },

        getActivePharmacy: async () => {
          set({ loading: true, error: null });
          try {
            const pharmacy = await pharmacyApi.getActivePharmacy();
            if (pharmacy) {
              // If the backend returns the stale duplicate, re-select the correct one
              const swappedId = applyPharmacyIdSwap(pharmacy.id);
              if (swappedId !== pharmacy.id) {
                await get().setActivePharmacy(swappedId);
                return;
              }
              set({
                activePharmacy: pharmacy,
                pharmacyId: pharmacy.id,
                selectedPharmacyId: pharmacy.id,
                loading: false,
              });
            } else {
              // If no active pharmacy, try to get the list and let backend auto-select
              const pharmacies = get().pharmacies;
              if (pharmacies.length === 0) {
                // Fetch pharmacies if we don't have them
                const fetchedPharmacies = await pharmacyApi.getUserPharmacies();
                set({ pharmacies: fetchedPharmacies });

                // Try getting active pharmacy again (backend should auto-select now)
                const activePharmacy = await pharmacyApi.getActivePharmacy();
                if (activePharmacy) {
                  set({
                    activePharmacy,
                    pharmacyId: activePharmacy.id,
                    selectedPharmacyId: activePharmacy.id,
                    loading: false,
                  });
                } else {
                  set({
                    activePharmacy: null,
                    pharmacyId: null,
                    selectedPharmacyId: null,
                    loading: false,
                  });
                }
              } else {
                set({
                  activePharmacy: null,
                  pharmacyId: null,
                  selectedPharmacyId: null,
                  loading: false,
                });
              }
            }
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Failed to get active pharmacy',
              loading: false,
            });
          }
        },

        clearError: () => set({ error: null }),

        reset: () => set({ ...initialState }),

        setUserId: (userId: string) => {
          const currentUserId = get().userId;
          if (currentUserId && currentUserId !== userId) {
            // User changed, reset pharmacy data
            set({ ...initialState });
          }
          set({ userId });
        },
      }),
      {
        name: 'pharmacy-store',
        partialize: (state) => ({
          activePharmacy: state.activePharmacy,
          userId: state.userId,
        }),
        onRehydrateStorage: () => (state, error) => {
          // After rehydration, ensure computed properties are set synchronously
          if (!error && state) {
            if (state.activePharmacy) {
              // Auto-swap stale duplicate pharmacy IDs
              state.activePharmacy.id = applyPharmacyIdSwap(state.activePharmacy.id);

              // Validate pharmacy ID format (MongoDB ObjectID should be 24 hex characters)
              const isValidId = /^[a-f\d]{24}$/i.test(state.activePharmacy.id);

              if (!isValidId) {
                console.error(
                  'PharmacyStore: Invalid pharmacy ID detected during rehydration:',
                  state.activePharmacy.id,
                );
                // Clear invalid data
                state.activePharmacy = null;
                state.pharmacyId = null;
                state.selectedPharmacyId = null;
              } else {
                // Set computed properties immediately
                state.pharmacyId = state.activePharmacy.id;
                state.selectedPharmacyId = state.activePharmacy.id;
              }
            }
            // Mark as hydrated
            state.isHydrated = true;
            hasHydrated = true;
          } else {
            // Even on error or no state, mark as hydrated to unblock components
            if (state) {
              state.isHydrated = true;
            }
            hasHydrated = true;
          }
        },
      },
    ),
    {
      name: 'PharmacyStore',
    },
  ),
);
